/**
 * ManagePlatformModals — bundles all 21 modals that ManagePlatform renders.
 *
 * Same `ctx` props bag pattern as <TabRouter />: ManagePlatform owns the state
 * (modal-open flags, selected entities, form values) and the mutation
 * functions; this file is intentionally dumb — it just wires them onto the
 * right modal components.
 *
 * Extracted in iter-81 to shrink ManagePlatform.js below the 1100-line threshold.
 */
import StoreDetailsModal from './modals/StoreDetailsModal';
import StoreRejectModal from './modals/StoreRejectModal';
import StoreDeactivateModal from './modals/StoreDeactivateModal';
import ModificationModal from './modals/ModificationModal';
import BanUserModal from './modals/BanUserModal';
import AgentRejectModal from './modals/AgentRejectModal';
import { NumberDeleteModal, NumberBlockModal, NumberEditModal } from './modals/NumberActionModals';
import { EditUserModal, ResetPasswordModal, DeleteUserModal, AddUserModal, ResetLinkModal } from './modals/UserActionModals';
import InviteCodeModal from './modals/InviteCodeModal';

export default function ManagePlatformModals({ ctx }) {
  return (
    <>
      {/* ===================== Store modals ===================== */}
      <StoreDetailsModal
        open={ctx.showStoreDetails}
        onOpenChange={ctx.setShowStoreDetails}
        selectedStore={ctx.selectedStore}
        STORE_TYPES={ctx.STORE_TYPES}
        BUSINESS_TYPES={ctx.BUSINESS_TYPES}
        isPdfDocument={ctx.isPdfDocument}
        isImageDocument={ctx.isImageDocument}
        openDocument={ctx.openDocument}
        formatDate={ctx.formatDate}
        setShowModificationModal={ctx.setShowModificationModal}
        setShowRejectModal={ctx.setShowRejectModal}
        approveStore={ctx.approveStore}
        processingStore={ctx.processingStore}
        language={ctx.language}
        onStop={() => ctx.setShowDeactivateModal(true)}
        onArchive={ctx.archiveStore}
        onCascadeDelete={ctx.cascadeDeleteStore}
      />

      <StoreDeactivateModal
        open={ctx.showDeactivateModal}
        onOpenChange={(v) => {
          ctx.setShowDeactivateModal(v);
          if (!v) ctx.setDeactivateReason('');
        }}
        selectedStore={ctx.selectedStore}
        reason={ctx.deactivateReason}
        setReason={ctx.setDeactivateReason}
        onConfirm={() => ctx.selectedStore && ctx.deactivateStore(ctx.selectedStore.id, ctx.deactivateReason)}
        processing={ctx.processingStore === ctx.selectedStore?.id}
        language={ctx.language}
      />

      <StoreRejectModal
        open={ctx.showRejectModal}
        onOpenChange={ctx.setShowRejectModal}
        selectedStore={ctx.selectedStore}
        storeRejectReasons={ctx.storeRejectReasons}
        selectedRejectReason={ctx.selectedRejectReason}
        setSelectedRejectReason={ctx.setSelectedRejectReason}
        rejectReason={ctx.rejectReason}
        setRejectReason={ctx.setRejectReason}
        onCancel={() => {
          ctx.setShowRejectModal(false);
          ctx.setRejectReason('');
          ctx.setSelectedRejectReason('');
        }}
        onConfirm={ctx.rejectStore}
        processingStore={ctx.processingStore}
      />

      <ModificationModal
        open={ctx.showModificationModal}
        onOpenChange={ctx.setShowModificationModal}
        selectedStore={ctx.selectedStore}
        storeEditReasons={ctx.storeEditReasons}
        selectedModificationReason={ctx.selectedModificationReason}
        setSelectedModificationReason={ctx.setSelectedModificationReason}
        modificationMessage={ctx.modificationMessage}
        setModificationMessage={ctx.setModificationMessage}
        onCancel={() => {
          ctx.setShowModificationModal(false);
          ctx.setModificationMessage('');
          ctx.setSelectedModificationReason('');
        }}
        onConfirm={ctx.requestModification}
        processingStore={ctx.processingStore}
      />

      {/* ===================== User Management modals ===================== */}
      <EditUserModal
        open={ctx.showEditUserModal}
        onOpenChange={ctx.setShowEditUserModal}
        selectedUser={ctx.selectedUser}
        editUserData={ctx.editUserData}
        setEditUserData={ctx.setEditUserData}
        onCancel={ctx.cancelEdit}
        onSubmit={ctx.updateUserData}
        processingUser={ctx.processingUser}
      />

      <ResetPasswordModal
        open={ctx.showResetPasswordModal}
        onOpenChange={ctx.setShowResetPasswordModal}
        selectedUser={ctx.selectedUser}
        newPassword={ctx.newPassword}
        setNewPassword={ctx.setNewPassword}
        onCancel={ctx.cancelResetPassword}
        onSubmit={ctx.resetUserPassword}
        processingUser={ctx.processingUser}
      />

      <DeleteUserModal
        open={ctx.showDeleteUserModal}
        onOpenChange={ctx.setShowDeleteUserModal}
        selectedUser={ctx.selectedUser}
        onCancel={ctx.cancelDelete}
        onSubmit={ctx.deleteUser}
        processingUser={ctx.processingUser}
      />

      <BanUserModal
        open={ctx.showBanUserModal}
        onOpenChange={ctx.setShowBanUserModal}
        selectedUser={ctx.selectedUser}
        banReason={ctx.banReason}
        setBanReason={ctx.setBanReason}
        onCancel={ctx.cancelBan}
        onConfirm={() => ctx.toggleBanUser(true)}
        processingUser={ctx.processingUser}
      />

      <AddUserModal
        open={ctx.showAddUserModal}
        onOpenChange={ctx.setShowAddUserModal}
        language={ctx.language}
        newUserData={ctx.newUserData}
        setNewUserData={ctx.setNewUserData}
        onCancel={ctx.cancelAddUser}
        onSubmit={ctx.createNewUser}
        processingUser={ctx.processingUser}
      />

      <ResetLinkModal
        open={ctx.showResetLinkModal}
        onOpenChange={ctx.setShowResetLinkModal}
        resetLinkData={ctx.resetLinkData}
        onClose={ctx.cancelResetLink}
      />

      {/* ===================== Agent modals ===================== */}
      <InviteCodeModal
        open={ctx.showCreateCodeModal}
        onOpenChange={ctx.setShowCreateCodeModal}
        newCodeMaxUses={ctx.newCodeMaxUses}
        setNewCodeMaxUses={ctx.setNewCodeMaxUses}
        newCodeExpiresDays={ctx.newCodeExpiresDays}
        setNewCodeExpiresDays={ctx.setNewCodeExpiresDays}
        onCancel={() => ctx.setShowCreateCodeModal(false)}
        onSubmit={ctx.handleCreateInvitationCode}
        creatingCode={ctx.creatingCode}
      />

      <AgentRejectModal
        open={ctx.showRejectAgentModal}
        onOpenChange={ctx.setShowRejectAgentModal}
        selectedAgentRequest={ctx.selectedAgentRequest}
        agentRejectReason={ctx.agentRejectReason}
        setAgentRejectReason={ctx.setAgentRejectReason}
        onCancel={() => {
          ctx.setShowRejectAgentModal(false);
          ctx.setSelectedAgentRequest(null);
          ctx.setAgentRejectReason('');
        }}
        onConfirm={ctx.handleConfirmRejectAgent}
        processingAgent={ctx.processingAgent}
      />

      {/* ===================== Number modals ===================== */}
      <NumberDeleteModal
        open={ctx.showDeleteNumberModal}
        onOpenChange={ctx.setShowDeleteNumberModal}
        selectedNumber={ctx.selectedNumber}
        onCancel={() => { ctx.setShowDeleteNumberModal(false); ctx.setSelectedNumber(null); }}
        onConfirm={() => ctx.deleteNumber(ctx.selectedNumber?.id)}
        processingNumber={ctx.processingNumber}
      />

      <NumberBlockModal
        open={ctx.showBlockNumberModal}
        onOpenChange={ctx.setShowBlockNumberModal}
        selectedNumber={ctx.selectedNumber}
        onCancel={() => { ctx.setShowBlockNumberModal(false); ctx.setSelectedNumber(null); }}
        onConfirm={() => ctx.blockNumber(ctx.selectedNumber?.id)}
        processingNumber={ctx.processingNumber}
      />

      <NumberEditModal
        open={ctx.showEditNumberModal}
        onOpenChange={ctx.setShowEditNumberModal}
        selectedNumber={ctx.selectedNumber}
        onClose={() => { ctx.setShowEditNumberModal(false); ctx.setSelectedNumber(null); }}
      />

      {/* NOTE: AddStoreTypeModal / EditStoreTypeModal / AddCategoryModal / EditCategoryModal /
          AddSubcategoryModal / EditSubcategoryModal were removed in iter-82.
          The full CRUD for StoreTypes / Categories / Subcategories now lives in dedicated
          pages: StoreTypesManagement.js + CategoriesManagement.js. They were never opened
          from any rendered button inside ManagePlatform.js — only the modal renderers were
          here, with no trigger. Cleanly removed. */}
    </>
  );
}
